// app/src/lib/start/ga-consent.js

'use strict';
const appSwitcher = require('./app-switcher');

function toSnakeCase(str) {
	if (typeof str !== 'string') str = String(str || '');

	return str
		.normalize('NFD')                    // decompose accented chars: é → e + `
		.replace(/[\u0300-\u036f]/g, '')     // remove accents/diacritics
		.trim()                              // remove leading/trailing spaces
		.replace(/([a-z0-9])([A-Z])/g, '$1_$2')  // camelCase → camel_Case
		.replace(/[\s-]+/g, '_')            // spaces and hyphens → underscore
		.replace(/[^\w@+._]+/g, '')          // remove all except: a-zA-Z0-9_ @ + .
		.toLowerCase();
}

function convertToSnakeCase(obj) {
	const result = {};
	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			const snakeKey = toSnakeCase(key),
				value = obj[key];

			result[snakeKey] = typeof value === 'string' ? toSnakeCase(value) : value;
		}
	}
	return result;
}

function setConsentCookie(config, prefs) {
	const cookieDomain = config.cookieDomain ? `domain=${config.cookieDomain} ` : '';
	document.cookie = `cookie_preferences=${encodeURIComponent(JSON.stringify(prefs))}; path=/; ${cookieDomain}SameSite=Lax; max-age=31536000`;
}

function getConsentCookie() {
	const cookie = document.cookie.split('; ').find(c => c.startsWith('cookie_preferences='));
	return cookie ? JSON.parse(decodeURIComponent(cookie.split('=')[1])) : null;
}

function injectGAScript(config) {
	if (window.gtag) return;
	window.dataLayer = window.dataLayer || [];
	function gtag() { window.dataLayer.push(arguments); }
	window.gtag = gtag;
	gtag('set', 'linker', { 'domains': ['www.mindmup.com', 'app.mindmup.com'] });
	gtag('js', new Date());
	gtag('config', config.googleMeasureId, {
		send_page_view: true,
	});

	const script = document.createElement('script');
	script.src = `https://www.googletagmanager.com/gtag/js?id=${config.googleMeasureId}`;
	script.async = true;
	document.head.appendChild(script);
}

function createConsentPreferencesModal(config) {
	const modal = document.createElement('div'),
		card = document.createElement('div');

	modal.id = 'cookie-modal';
	card.id = 'cookie-card';

	card.innerHTML = `
		<button id="close-cookie-modal" aria-label="Close">&times;</button>

		<h2 style="margin-top: 0;font-size: 20px;font-weight: 600;">Cookie Preferences</h2>
        <p styl="text-align:justify">When you visit any of our websites, it may store or retrieve information on your browser, mostly in the form of cookies. This information might be about you, your preferences, or your device and is mostly used to make the site work as you expect it to. The information does not usually directly identify you, but it can give you a more personalized experience. Because we respect your right to privacy, you can choose not to allow some types of cookies. Please note, blocking some types of cookies may impact your experience of the site and the services we are able to offer.</p>
		<p style="font-size: 0.95rem;">Customize your cookie choices:</p>
		<form id="cookie-form" style="margin: 1rem 0 0;">
			<label style="display: block; margin-bottom: 0.5rem;">
				<input type="checkbox" checked disabled> Strictly Necessary Cookies
			</label>
			<label style="display: block; margin-bottom: 0.5rem;">
				<input type="checkbox" id="perf-cb" checked> Performance Cookies
			</label>
			<label style="display: block; margin-bottom: 1rem;">
				<input type="checkbox" id="func-cb" checked> Functional Cookies
			</label>
			<div style="display: flex; justify-content: flex-end; gap: 0.5rem;">
				<button type="button" id="save-cookie-prefs">Save Preferences</button>
			</div>
		</form>
	`;

	modal.appendChild(card);
	document.body.appendChild(modal);

	document.getElementById('save-cookie-prefs').onclick = () => {
		const perf = document.getElementById('perf-cb').checked,
			func = document.getElementById('func-cb').checked;

		setConsentCookie(config, {
			necessary: true,
			performance: perf,
			functional: func
		});

		injectGAScript(config);
		modal.remove();
		document.getElementById('ga-consent-banner').remove();
	};

	document.getElementById('close-cookie-modal').onclick = () => {
		modal.remove();
	};
}

function createConsentBanner(config) {
	if (localStorage.getItem('cookie_preferences')) return;

	const banner = document.createElement('div');
	banner.id = 'ga-consent-banner';

	banner.innerHTML = `
		<div style="flex: 1; min-width: 250px; font-size:15px;">
			We use cookies to enhance user experience and to analyze performance and traffic on our website.
			<a href="https://www.mindmup.com/resources/privacy_policy.html" style="color: #9cf;">Learn more</a>.
		</div>
		<div style="display: flex; gap: 0.5rem; margin-top: 0.5rem;">
			<button id="accept-all-cookies">Accept All</button>
			<button id="open-cookie-settings">Cookie Settings</button>
		</div>
	`;

	document.body.appendChild(banner);

	document.getElementById('accept-all-cookies').onclick = () => {
		setConsentCookie(config, {
			necessary: true,
			performance: true,
			functional: true
		});
		injectGAScript(config);
		banner.remove();
	};

	document.getElementById('open-cookie-settings').onclick = () => {
		createConsentPreferencesModal(config);
	};
}

function initGAConsent(config) {
	try { appSwitcher.initAppSwitcher(config); } catch (e) { console.warn('App switcher init failed', e); }

	if (!config || !config.googleMeasureId) {
		console.warn('GA measurement ID missing');
		return;
	}

	const prefs = getConsentCookie();

	if (prefs) {
		injectGAScript(config);
	} else {
		createConsentBanner(config);
	}
}

function trackEvent({ action, value = null, currency = 'USD', ...params }) {
	if (typeof window === 'undefined' || !window.gtag) return;

	const snakeCaseParams = convertToSnakeCase({
		value: value !== null ? value : undefined,
		currency: value !== null ? currency : undefined,
		...params
	});

	Object.keys(snakeCaseParams).forEach(key => {
		if (snakeCaseParams[key] === undefined || snakeCaseParams[key] === null) {
			delete snakeCaseParams[key];
		}
	});

	window.gtag('event', toSnakeCase(action), snakeCaseParams);
}

function trackPageView(path = location.pathname) {
	if (!window.gtag) return;
	window.gtag('event', 'page_view', { page_path: path });
}

function trackClick(selector, eventOptions) {
	document.querySelectorAll(selector).forEach(el => {
		el.addEventListener('click', () => trackEvent(eventOptions));
	});
}

module.exports = {
	initGAConsent,
	trackEvent,
	trackPageView,
	trackClick
};
